import {
  CheckOutlined,
  CommentOutlined,
  CopyOutlined,
  ExportOutlined,
  ReadOutlined,
  RocketOutlined,
  UserOutlined,
} from "@ant-design/icons";
import { useQuery } from "@tanstack/react-query";
import { type UIMessage } from "ai";
import { Avatar, Button, message, Tooltip } from "antd";
import { useState, type ReactNode } from "react";
import { useNavigate } from "react-router-dom";
import { type Components } from "streamdown";

import { chatActions } from "@fitness-app/app-store";

import { useUserRole } from "~/hooks/trainer/useUserRole";
import { copyTextToClipboard } from "~/modules/Sales/SalesAgent/messageUtils";
import { analyticsService, supabase, useAppDispatch } from "~/store/initializeStore";

import { citedHelpSources, type HelpSource } from "./helpSources";

// TRE-2313 — bogatsza treść odpowiedzi asystenta:
// - klientka zapisana przez agenta jako `[Imię](/trainee/details/<id>)` to chip z avatarem,
//   który otwiera jej profil w panelu;
// - `[Otwórz czat](/chat/trainee/<id>)` (wyniki wyszukiwania w czatach, TRE-2369) otwiera
//   okienko czatu tej klientki, tak jak przycisk wiadomości w jej profilu;
// - materiały z centrum pomocy, które agent zacytował linkiem, to karty pod odpowiedzią
//   z linkiem do skopiowania dla klientki. Dane kart idą z wyników narzędzi, nie z tekstu.

const TRAINEE_PATH = /^\/trainee\/details\/([0-9a-f-]{36})\/?$/i;
const TRAINEE_CHAT_PATH = /^\/chat\/trainee\/([0-9a-f-]{36})\/?$/i;

const useTraineeAvatar = (traineeId: string) =>
  useQuery({
    queryKey: ["trainerAssistantTraineeAvatar", traineeId],
    staleTime: 10 * 60_000,
    queryFn: async () => {
      const { data } = await supabase
        .from("trainee")
        .select("user:userId(avatarUrl)")
        .eq("id", traineeId)
        .maybeSingle();
      return (data?.user as { avatarUrl: string | null } | null)?.avatarUrl ?? null;
    },
  }).data;

const ClientLinkChip = ({ traineeId, children }: { traineeId: string; children: ReactNode }) => {
  const navigate = useNavigate();
  const avatarUrl = useTraineeAvatar(traineeId);
  return (
    <button
      type="button"
      className="not-typeset inline-flex h-[22px] cursor-pointer items-center gap-1 rounded-md border-0 bg-white py-0 pl-[3px] pr-1.5 align-middle text-[0.93em] font-medium leading-none text-blue-700 ring-1 ring-inset ring-blue-200 transition-colors hover:bg-blue-50"
      onClick={() => {
        analyticsService.track("trainer_assistant_client_opened", { traineeId });
        navigate(`/trainee/details/${traineeId}`);
      }}
    >
      <Avatar size={16} className="shrink-0" src={avatarUrl || undefined} icon={<UserOutlined />} />
      {children}
    </button>
  );
};

const OpenChatChip = ({ traineeId, children }: { traineeId: string; children: ReactNode }) => {
  const dispatch = useAppDispatch();
  const { isHeadCoach } = useUserRole();
  const [opening, setOpening] = useState(false);

  const openChat = async () => {
    setOpening(true);
    try {
      const { data, error } = await supabase.from("trainee").select("userId").eq("id", traineeId).maybeSingle();
      if (error) throw error;
      if (!data?.userId) {
        void message.warning("Ta klientka nie ma czatu.");
        return;
      }
      await dispatch(chatActions.findAndAddChannelToList({ userId: data.userId, isHeadCoach })).unwrap();
      analyticsService.track("trainer_assistant_chat_opened", { traineeId });
    } catch {
      void message.warning("Nie udało się otworzyć czatu tej klientki.");
    } finally {
      setOpening(false);
    }
  };

  return (
    <button
      type="button"
      disabled={opening}
      className="not-typeset inline-flex h-[22px] cursor-pointer items-center gap-1 rounded-md border-0 bg-white px-1.5 py-0 align-middle text-[0.93em] font-medium leading-none text-blue-700 ring-1 ring-inset ring-blue-200 transition-colors hover:bg-blue-50 disabled:cursor-wait disabled:opacity-60"
      onClick={() => void openChat()}
    >
      <CommentOutlined className="text-[12px]" />
      {children}
    </button>
  );
};

export const assistantMarkdownComponents = {
  a: ({ href, children, node: _node, ...props }) => {
    const traineeId = href?.match(TRAINEE_PATH)?.[1];
    if (traineeId) return <ClientLinkChip traineeId={traineeId}>{children}</ClientLinkChip>;
    const chatTraineeId = href?.match(TRAINEE_CHAT_PATH)?.[1];
    if (chatTraineeId) return <OpenChatChip traineeId={chatTraineeId}>{children}</OpenChatChip>;
    return (
      <a href={href} target="_blank" rel="noopener noreferrer" {...props}>
        {children}
      </a>
    );
  },
} satisfies Components;

// Instrukcje istnieją parami (aplikacja i przeglądarka) z tym samym tytułem; rozróżnia je
// tylko `?platform=` w adresie.
const platformLabel = (url: string) => {
  const platform = url.match(/[?&]platform=(mobile|web)/)?.[1];
  return platform === "mobile" ? "Aplikacja" : platform === "web" ? "Przeglądarka" : null;
};

const SourceCard = ({ source }: { source: HelpSource }) => {
  const [copied, setCopied] = useState(false);
  const isUpdate = source.kind === "update";
  return (
    <div className="flex items-start gap-3 rounded-lg border border-solid border-gray-200 bg-white px-3 py-2.5">
      <div className="mt-0.5 flex h-7 w-7 shrink-0 items-center justify-center rounded-md bg-gray-100 text-gray-500">
        {isUpdate ? <RocketOutlined /> : <ReadOutlined />}
      </div>
      <div className="min-w-0 flex-1">
        <div className="text-[11px] font-medium uppercase tracking-wide text-gray-400">
          {[isUpdate ? "Co nowego" : "Instrukcja", platformLabel(source.url)].filter(Boolean).join(" · ")}
        </div>
        <div className="line-clamp-2 text-[13px] font-medium leading-5 text-gray-900">{source.title}</div>
        {source.summary ? (
          <div className="line-clamp-2 text-xs leading-[18px] text-gray-500">{source.summary}</div>
        ) : null}
      </div>
      <div className="flex shrink-0 items-center gap-0.5">
        <Tooltip title={copied ? "Skopiowano" : "Kopiuj link dla klientki"} mouseEnterDelay={0.3}>
          <Button
            type="text"
            size="small"
            className="!h-7 !w-7 !text-gray-400 hover:!text-gray-700"
            icon={copied ? <CheckOutlined /> : <CopyOutlined />}
            aria-label="Kopiuj link dla klientki"
            onClick={() => {
              void copyTextToClipboard(source.url);
              analyticsService.track("trainer_assistant_source_copied", { slug: source.slug, kind: source.kind });
              setCopied(true);
              window.setTimeout(() => setCopied(false), 1500);
            }}
          />
        </Tooltip>
        <Tooltip title="Otwórz" mouseEnterDelay={0.3}>
          <Button
            type="text"
            size="small"
            className="!h-7 !w-7 !text-gray-400 hover:!text-gray-700"
            icon={<ExportOutlined />}
            href={source.url}
            target="_blank"
            rel="noopener noreferrer"
            aria-label="Otwórz materiał"
            onClick={() =>
              analyticsService.track("trainer_assistant_source_opened", { slug: source.slug, kind: source.kind })
            }
          />
        </Tooltip>
      </div>
    </div>
  );
};

export const HelpSources = ({ message }: { message: UIMessage }) => {
  const sources = citedHelpSources(message);
  if (!sources.length) return null;
  return (
    <div className="not-typeset mt-3 flex flex-col gap-1.5">
      {sources.map((source) => (
        <SourceCard key={source.url} source={source} />
      ))}
    </div>
  );
};
